import type { AuthTokens } from '@repo/api-contracts';

/** REST 응답 봉투(ApiResponse) 중 재발급에 필요한 부분. */
interface RefreshEnvelope {
  readonly success: boolean;
  readonly data: AuthTokens | null;
}

export interface TokenRefresherDeps {
  /** API 서버 기원(origin). import.meta 의존을 호출부(entities)로 밀어내 테스트를 가능하게 한다. */
  readonly apiUrl: string;
  /** 스토어에서 현재 리프레시 토큰을 읽는다. 없으면 재발급을 시도하지 않는다. */
  readonly getRefreshToken: () => string | null;
  /** 재발급 성공 시 새 토큰 쌍을 스토어에 반영한다. (회전 — 리프레시 토큰도 바뀐다) */
  readonly onTokens: (tokens: AuthTokens) => void;
}

/** 재발급 시도. true = 새 토큰 확보, false = 세션 만료(재로그인 필요). */
export type TokenRefresher = () => Promise<boolean>;

/**
 * 액세스 토큰 재발급기 — REST /api/auth/refresh 를 호출한다.
 *
 * tRPC 대신 REST 를 쓰는 이유: tRPC 클라이언트로 부르면 재발급 요청 자체가
 * 재발급 링크를 다시 타는 재귀 구조가 된다.
 *
 * 단일 비행(single-flight): 여러 요청이 동시에 401 을 맞아도 재발급은 한 번만
 * 나간다 — 회전 유예 창(서버 GRACE) 안에서도 불필요한 회전 경쟁을 만들지 않는다.
 */
export const createTokenRefresher = (deps: TokenRefresherDeps): TokenRefresher => {
  let inFlight: Promise<boolean> | null = null;

  return () => {
    if (inFlight) {
      return inFlight;
    }

    const refreshToken = deps.getRefreshToken();

    if (!refreshToken) {
      return Promise.resolve(false);
    }

    inFlight = (async (): Promise<boolean> => {
      try {
        const response = await fetch(`${deps.apiUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
          return false;
        }

        const body = (await response.json()) as RefreshEnvelope;

        if (!body.success || !body.data) {
          return false;
        }

        deps.onTokens(body.data);
        return true;
      } catch {
        // 네트워크 오류 — 세션 만료 취급. (다음 요청이 다시 시도할 수 있다)
        return false;
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  };
};
